import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  LinearProgress,
  Pagination,
  Tooltip,
  Typography,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { ClearFiltersButton, CommonList, ListSelectionMode } from '../common/components';
import { CatalogFilters } from './components';
import type { Catalog } from '../../types/catalog';
import { useCatalogData } from './useCatalogData';

export default function CatalogPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const companyId = id ? parseInt(id) : null;

  const { data, pagination, filters, crud, dialogs } = useCatalogData({ companyId });

  if (data.isFirstLoad) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (data.error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {data.error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      <Box
        sx={{
          position: 'sticky',
          top: 64,
          zIndex: 10,
          backgroundColor: 'background.default',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          py: 1,
          mb: 0.5,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/companies/${companyId}/home`)}
          size="small"
        >
          На головну
        </Button>
        <Typography
          variant="button"
          component="h1"
          noWrap
          sx={{
            minWidth: 0,
            pr: { xs: '9px', sm: '10px' },
            color: 'primary.main',
          }}
        >
          Каталоги
        </Typography>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: { xs: 1, sm: 2 },
          }}
        >
          <Tooltip title="Показати/сховати фільтри">
            <IconButton
              size="small"
              onClick={() => filters.setShowFilters(!filters.showFilters)}
              color={filters.showFilters || filters.hasActiveFilters ? 'primary' : 'default'}
            >
              <SearchIcon />
            </IconButton>
          </Tooltip>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate(`/companies/${companyId}/catalogs/create`)}
          >
            Створити
          </Button>
        </Box>
      </Box>

      <CatalogFilters
        show={filters.showFilters}
        filterName={filters.filterName}
        onFilterNameChange={filters.setFilterName}
        showOnlyActive={filters.showOnlyActive}
        onShowOnlyActiveChange={filters.setShowOnlyActive}
        onClearFilters={filters.handleClearFilters}
      />

      {data.loading && <LinearProgress sx={{ mb: 2 }} />}

      <Box sx={{ opacity: data.loading ? 0.6 : 1, transition: 'opacity 0.2s' }}>
        {data.catalogs.length === 0 && !data.loading ? (
          <Box
            sx={{
              p: 6,
              textAlign: 'center',
              backgroundColor: 'background.default',
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            {filters.hasActiveFilters ? (
              <>
                <Typography variant="h6" gutterBottom>
                  За вашим пошуком нічого не знайдено
                </Typography>
                <ClearFiltersButton onClick={filters.handleClearFilters} />
              </>
            ) : (
              <>
                <Typography variant="h5" gutterBottom>
                  Каталогів ще немає
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  Створіть каталог, щоб поділитися товарами та цінами з клієнтами
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate(`/companies/${companyId}/catalogs/create`)}
                >
                  Створити
                </Button>
              </>
            )}
          </Box>
        ) : (
          <>
            <CommonList<Catalog>
              items={data.catalogs}
              toListItem={(item) => ({
                id: item.id,
                primary: item.name,
                secondary: item.accessMode === 'PUBLIC' ? 'Публічний' : 'Обмежений доступ',
                tertiary: item.itemCount !== undefined ? `${item.itemCount} тов.` : undefined,
                archived: !!item.deactivatedDate,
              })}
              onDelete={crud.handleDeleteCatalog}
              onRestore={crud.handleRestoreCatalog}
              renderActions={(item) => (
                <Tooltip title="Перейти до каталогу">
                  <IconButton
                    aria-label="open catalog"
                    onClick={() => navigate(`/catalogs/${item.id}/view`)}
                  >
                    <OpenInNewIcon />
                  </IconButton>
                </Tooltip>
              )}
              onEdit={(item) => navigate(`/companies/${companyId}/catalogs/${item.id}/edit`)}
              selectionMode={ListSelectionMode.VIEW}
              showSelection={false}
              emptyMessage="Каталоги не знайдено"
            />

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                {...pagination}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          </>
        )}
      </Box>

      {dialogs.confirmDialog}
    </Box>
  );
}
